<template>
  <div id="top" class="home">
    <!-- ============ HERO ============ -->
    <section class="relative overflow-hidden bg-ink-900 text-white">
      <!-- Decorative brand glows -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute -top-24 -right-24 w-[28rem] h-[28rem] bg-kettelsen opacity-20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 -left-24 w-96 h-96 bg-kettelsen-700 opacity-20 rounded-full blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10 py-24 md:py-32">
        <div class="max-w-3xl">
          <span class="inline-block text-kettelsen font-bold uppercase tracking-widest text-xs sm:text-sm">
            {{ $t('hero.eyebrow') }}
          </span>
          <h1 class="text-4xl md:text-6xl font-extrabold leading-[1.1] mt-5">
            {{ $t('hero.title') }}
          </h1>
          <p class="text-lg md:text-xl text-white/70 mt-6 max-w-2xl leading-relaxed">
            {{ $t('hero.subtitle') }}
          </p>
          <p class="flex items-center gap-2 text-kettelsen font-semibold text-lg mt-7">
            <SparklesIcon class="h-5 w-5" />
            {{ $t('hero.tagline') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4 mt-9">
            <a href="#contact" class="btn-primary text-base px-8 py-4">
              {{ $t('hero.primaryCta') }}
              <ArrowRightIcon class="h-5 w-5" />
            </a>
            <a href="#process" class="btn-outline-light text-base px-8 py-4">
              {{ $t('hero.secondaryCta') }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ VALUE PILLARS ============ -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <div class="grid md:grid-cols-3 gap-8">
          <div
            v-for="(pillar, i) in pillars"
            :key="i"
            class="rounded-2xl border border-ink/5 bg-white p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            <div class="w-14 h-14 rounded-xl bg-kettelsen text-white flex items-center justify-center mb-6 shadow-md shadow-kettelsen/30">
              <component :is="pillarIcons[i]" class="h-7 w-7" />
            </div>
            <h3 class="text-xl font-bold text-ink mb-3">{{ pillar.title }}</h3>
            <p class="text-ink/60 leading-relaxed">{{ pillar.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ SERVICES ============ -->
    <section id="services" class="py-24 bg-gray-50">
      <div class="container-custom">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <span class="section-eyebrow">{{ $t('services.eyebrow') }}</span>
          <h2 class="text-3xl md:text-5xl font-extrabold text-ink mt-4 mb-5">{{ $t('services.title') }}</h2>
          <p class="text-lg text-ink/60">{{ $t('services.subtitle') }}</p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="(service, i) in services"
            :key="i"
            class="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl border border-ink/5 transition-all duration-300 hover:-translate-y-1"
          >
            <div class="w-14 h-14 rounded-xl bg-kettelsen/10 text-kettelsen flex items-center justify-center mb-6 group-hover:bg-kettelsen group-hover:text-white transition-colors duration-300">
              <component :is="serviceIcons[i]" class="h-7 w-7" />
            </div>
            <h3 class="text-lg font-bold text-ink mb-3">{{ service.title }}</h3>
            <p class="text-ink/60 text-sm leading-relaxed">{{ service.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ HOW IT WORKS ============ -->
    <section id="process" class="py-24 bg-white">
      <div class="container-custom">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <span class="section-eyebrow">{{ $t('process.eyebrow') }}</span>
          <h2 class="text-3xl md:text-5xl font-extrabold text-ink mt-4 mb-5">{{ $t('process.title') }}</h2>
          <p class="text-lg text-ink/60">{{ $t('process.subtitle') }}</p>
        </div>

        <div class="grid md:grid-cols-4 gap-8">
          <div v-for="(step, i) in steps" :key="i" class="relative">
            <div class="flex items-center gap-4 mb-5">
              <div class="w-12 h-12 rounded-full bg-kettelsen text-white flex items-center justify-center font-extrabold text-lg shrink-0">
                {{ i + 1 }}
              </div>
              <!-- connector line on desktop -->
              <div v-if="i < steps.length - 1" class="hidden md:block h-px flex-1 bg-gradient-to-r from-kettelsen/40 to-transparent"></div>
            </div>
            <h3 class="text-lg font-bold text-ink mb-2">{{ step.title }}</h3>
            <p class="text-ink/60 text-sm leading-relaxed">{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ WHY KETTELSEN ============ -->
    <section id="why" class="py-24 bg-ink-900 text-white relative overflow-hidden">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-1/2 -right-32 w-[30rem] h-[30rem] bg-kettelsen opacity-10 rounded-full blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10">
        <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <!-- Left: copy + bullets -->
          <div>
            <span class="inline-block text-kettelsen font-bold uppercase tracking-widest text-sm">{{ $t('why.eyebrow') }}</span>
            <h2 class="text-3xl md:text-5xl font-extrabold mt-4 mb-5">{{ $t('why.title') }}</h2>
            <p class="text-lg text-white/70 mb-8">{{ $t('why.subtitle') }}</p>
            <ul class="space-y-4">
              <li v-for="(point, i) in points" :key="i" class="flex items-start gap-3">
                <CheckCircleIcon class="h-6 w-6 text-kettelsen shrink-0 mt-0.5" />
                <span class="text-white/80">{{ point }}</span>
              </li>
            </ul>
          </div>

          <!-- Right: stats -->
          <div>
            <div class="grid grid-cols-2 gap-4 sm:gap-6">
              <div
                v-for="(stat, i) in stats"
                :key="i"
                class="rounded-2xl bg-white/5 border border-white/10 p-6 sm:p-8 text-center"
              >
                <div class="text-3xl sm:text-4xl font-extrabold text-kettelsen">{{ stat.value }}</div>
                <div class="text-white/60 text-sm mt-2">{{ stat.label }}</div>
              </div>
            </div>
            <p class="text-white/30 text-xs mt-4 text-center">{{ $t('why.statsNote') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ BOOKING ============ -->
    <section id="contact" class="py-24 bg-gray-50">
      <div class="container-custom">
        <div class="text-center max-w-3xl mx-auto mb-12">
          <span class="section-eyebrow">{{ $t('booking.eyebrow') }}</span>
          <h2 class="text-3xl md:text-5xl font-extrabold text-ink mt-4 mb-5">{{ $t('booking.title') }}</h2>
          <p class="text-lg text-ink/60">{{ $t('booking.subtitle') }}</p>
        </div>

        <div class="max-w-4xl mx-auto">
          <div class="bg-white rounded-3xl shadow-xl border border-ink/5 p-3 sm:p-6 md:p-8">
            <div class="rounded-2xl overflow-hidden">
              <!-- Google Calendar Appointment Scheduling -->
              <iframe
                :src="bookingEmbed"
                style="border: 0"
                width="100%"
                height="700"
                frameborder="0"
                title="Book a call with Kettelsen"
              ></iframe>
            </div>
            <p class="text-center text-ink/50 text-sm mt-5">
              {{ $t('booking.fallbackText') }}
              <a :href="bookingLink" target="_blank" rel="noopener" class="text-kettelsen font-semibold hover:underline ml-1">
                {{ $t('booking.fallbackCta') }}
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import {
  SparklesIcon,
  ArrowRightIcon,
  CheckBadgeIcon,
  Squares2X2Icon,
  BoltIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  GlobeAmericasIcon,
  HomeModernIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'HomeView',
  components: { SparklesIcon, ArrowRightIcon, CheckCircleIcon },
  setup() {
    const { tm } = useI18n()

    return {
      pillars: computed(() => tm('trust.items')),
      services: computed(() => tm('services.items')),
      steps: computed(() => tm('process.steps')),
      points: computed(() => tm('why.points')),
      stats: computed(() => tm('why.stats')),
      // Icon sets matched by index to the translated arrays
      pillarIcons: [CheckBadgeIcon, Squares2X2Icon, BoltIcon],
      serviceIcons: [MagnifyingGlassIcon, UserGroupIcon, GlobeAmericasIcon, HomeModernIcon],
      // Google Calendar appointment scheduling (resolved from calendar.app.google/WvEkWMimv1bHg7jcA)
      bookingEmbed: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ3YXqX1BG1Ao3CoiKTQX9j-qtT4lrKx7F1L80nGR54JH8Cnqw3tTiaFNQKh0Q5aWmTcnhLk3-mg?gv=true',
      bookingLink: 'https://calendar.app.google/WvEkWMimv1bHg7jcA'
    }
  }
}
</script>
